import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CompaniesService } from 'api';
import { globalAction } from 'common/actions';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useRefreshFirmCompanies } from 'services/hooks/firms';
import { useResponse } from 'services/hooks/useResponse';
import { DeleteCompanyParams, UseDeleteCompanyReturn } from './useDeleteCompany.types';

const DEFAULT_DELETE_COMPANY_ERROR_MESSAGE = 'An error occurred while deleting the Company';

const useDeleteCompany = (): UseDeleteCompanyReturn => {
  const [{ firmId: currentFirmId }, dispatch] = useStore() as UseStoreValues;
  const { processErrorResponse } = useResponse();
  const { refreshFirmCompanies } = useRefreshFirmCompanies();

  const { mutateAsync } = useMutation({
    mutationFn: ({ companyId }: DeleteCompanyParams) => CompaniesService.apiCompaniesDelete(companyId),
  });

  const deleteCompany = useCallback(
    async (params: DeleteCompanyParams) => {
      dispatch(globalAction.showLoadingProgress(true));

      try {
        await mutateAsync(params);
        await refreshFirmCompanies({ firmId: params.firmId ?? currentFirmId });
        return true;
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage: DEFAULT_DELETE_COMPANY_ERROR_MESSAGE,
          action: 'delete the Company',
        });
        return false;
      } finally {
        dispatch(globalAction.showLoadingProgress(false));
      }
    },
    [currentFirmId, dispatch, mutateAsync, processErrorResponse, refreshFirmCompanies]
  );

  return {
    deleteCompany,
  };
};

export default useDeleteCompany;
